import api from './api';

export interface TenantUnit {
  id?: number;
  roomNumber: string;
  companyName: string;
  representativeName: string;
  representativeImageUrl?: string;
  subName?: string;
  isOccupied?: boolean;
  tenantId?: number;
  tenant?: any;
}

export interface FloorData {
  floor: string;
  tenantCount: number;
  emptyCount: number;
  rooms: TenantUnit[];
}

export interface BulkFloorData {
  floors: {
    floor: string;
    rooms: TenantUnit[];
  }[];
}

class TenantUnitService {
  private baseURL = '/tenant-units';

  // 모든 층별 호수 정보 조회
  async getTenantUnits(): Promise<FloorData[]> {
    try {
      const response = await api.get(this.baseURL);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch tenant units:', error);
      throw error;
    }
  }

  // 특정 층의 호수 정보 조회
  async getFloorUnits(floor: string): Promise<FloorData> {
    try {
      const response = await api.get(`${this.baseURL}/floor/${encodeURIComponent(floor)}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to fetch floor ${floor} units:`, error);
      throw error;
    }
  }

  // 특정 층의 호수 정보 일괄 수정
  async updateFloorUnits(floor: string, rooms: TenantUnit[]): Promise<any> {
    try {
      const response = await api.put(`${this.baseURL}/floor/${encodeURIComponent(floor)}`, {
        rooms
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to update floor ${floor} units:`, error);
      throw error;
    }
  }

  // 호수 정보 대량 생성/업데이트 (초기 데이터 설정용)
  async bulkCreateUnits(data: BulkFloorData): Promise<any> {
    try {
      const response = await api.post(`${this.baseURL}/bulk`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to bulk create units:', error);
      throw error;
    }
  }

  // 새 호수 정보 생성
  async createUnit(unit: Omit<TenantUnit, 'id'> & { floor: string }): Promise<TenantUnit> {
    try {
      const response = await api.post(this.baseURL, unit);
      return response.data.data;
    } catch (error) {
      console.error('Failed to create unit:', error);
      throw error;
    }
  }

  // 호수 정보 수정
  async updateUnit(id: number, updates: Partial<TenantUnit>): Promise<TenantUnit> {
    try {
      const response = await api.put(`${this.baseURL}/${id}`, updates);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to update unit ${id}:`, error);
      throw error;
    }
  }

  // 호수 정보 삭제
  async deleteUnit(id: number): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/${id}`);
    } catch (error) {
      console.error(`Failed to delete unit ${id}:`, error);
      throw error;
    }
  }

  // 기본 층별 호수 데이터 생성 (초기 설정용)
  async initializeDefaultData(): Promise<any> {
    const defaultFloors = [
      '13F', '12F', '11F', '10F', '09F', '08F', '07F', '06F', '05F', '04F', '03F', '02F', '01F', 'B01'
    ];

    const sampleData: BulkFloorData = {
      floors: defaultFloors.map(floor => ({
        floor,
        rooms: floor === '12F' ? [
          // 12층에만 샘플 데이터 추가
          { roomNumber: '1201호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1202호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1203호', companyName: '회사명을 입력하세요.', representativeName: '회사명을 입력하세요.' },
          { roomNumber: '1204호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1205호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1206호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1207호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1208호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1209호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1210호', companyName: '(주)에이치나인', representativeName: 'HNINE' },
          { roomNumber: '1211호', companyName: '회사명을 입력하세요.', representativeName: '회사명을 입력하세요.' }
        ] : []
      }))
    };

    return this.bulkCreateUnits(sampleData);
  }
}

export default new TenantUnitService();